/**
 * Utility functions for highlighting search query matches in the global search results.
 *
 * This module provides functions to:
 * - Parse and clean phone numbers by removing formatting characters
 * - Parse email addresses into searchable parts
 * - Generate background color styles for highlighting matched text in search results
 *
 * The highlighting is applied to remote IDs, phone numbers, and email addresses
 * when they match the user's search query, making it easier for users to see
 * why a particular result was returned.
 */

/**
 * Parses an email address into searchable parts.
 *
 * @param {string} email - The email address to parse
 * @returns {string[]} An array of lowercase parts from the email:
 *   - If email is invalid or missing, returns an empty array
 *   - If email doesn't contain '@' and '.', returns the original email as a single-item array
 *   - Otherwise, splits the email into local parts (before @) and domain part (after @, before TLD)
 *   - Local parts are further split by periods
 */
export const parseEmailQuery = (query) => {
  if (!query) return [];
  if (!query.includes('@')) return [query.toLowerCase()];

  const [localPart, domain] = query.split('@');

  const localParts = localPart.split('.').map((part) => part.toLowerCase());

  if (!domain) return localParts;

  const domainParts = domain.split('.');
  const domainName = domainParts[0].toLowerCase();

  return [...localParts, domainName, ...domainParts.slice(1).map((part) => part.toLowerCase())];
};

export const REMOTE_ID_REGEX = /-/g; // Matches all dashes globally in a string

export const getRemoteIdBackgroundColor = (remoteId = '', query = '', queryType = 'all') => {
  return {
    backgroundColor:
      queryType === 'remoteId' || (queryType === 'all' && remoteId.toLowerCase().includes(query.toLowerCase()))
        ? '#fdf7dc'
        : 'transparent',
    width: 'fit-content',
  };
};

export const PHONE_REGEX = /[^\d]/g; // remove all non-numeric characters from phone numbers

export const phoneMatch = (phone = '', query = '') => {
  if (/[a-zA-Z]/.test(query)) return false;
  const cleanedPhone = phone.replace(PHONE_REGEX, '');
  const cleanedQuery = query.replace(PHONE_REGEX, '');

  if (!cleanedQuery) return false; // prevent empty query match

  return cleanedPhone.includes(cleanedQuery);
};

export const getPhoneBackgroundColor = (phone = '', query = '', queryType = 'all') => {
  return {
    backgroundColor: phoneMatch(phone, query) ? '#fdf7dc' : 'transparent',
    width: 'fit-content',
  };
};

export const getEmailBackgroundColor = (email = '', query = '', queryType = 'all') => {
  return {
    backgroundColor: parseEmailQuery(query).some((part) => email.toLowerCase().includes(part.toLowerCase()))
      ? '#fdf7dc'
      : 'transparent',
    width: 'fit-content',
  };
};

// Highlight style for matched items in tables
export const highlightStyle = {
  backgroundColor: '#fdf7dc',
  width: 'fit-content',
  padding: '0 2px',
};
